import {
  CatalogueProduct,
  ProductKey,
  ProductPrice,
  ProductPurchase,
} from "../types/billing";

/**
 * What the dashboard itself knows about its products, and nothing more.
 *
 * Names, run costs and prices belong to `GET /billing/products`. The page only knows where each
 * product lives and how its card looks, keyed by the backend's product key, plus one fallback
 * table for when the catalogue cannot be reached at all.
 */

/** Every key the backend may name, in the order the dashboard shows them. */
export const PRODUCT_KEYS: readonly ProductKey[] = [
  "signal_card",
  "signal_scan",
  "signal_validate",
  "signal_scan_me",
];

export const isProductKey = (value: unknown): value is ProductKey =>
  typeof value === "string" &&
  (PRODUCT_KEYS as readonly string[]).includes(value);

/** A product's page and its landing-page card. */
export interface ProductPage {
  /** The route the card's Select button goes to. */
  route: string;
  /**
   * The same route as it travels in `?return=` on the way back from Stripe. The payment-success
   * page only follows slugs from this table.
   */
  slug: string;
  /**
   * Where a run of the product is set up, when that is not `route` itself. A draft kept across
   * the trip to Stripe is restored there, so that is where the way back leads while one waits.
   */
  formRoute?: string;
  image: string;
  description: string;
}

export const PRODUCT_PAGES: Record<ProductKey, ProductPage> = {
  signal_card: {
    route: "/signal-card",
    slug: "signal-card",
    image: "/img/signal-card.webp",
    description:
      "Rapid public-signal triage for aligning with key market events. Advanced AI identifies trends and critical conditions for quick, informed decisions.",
  },
  signal_scan: {
    route: "/signal-scan",
    slug: "signal-scan",
    image: "/img/signal-scan.webp",
    description:
      "Decision-grade public-signal assessment of execution capability and collaboration risk. Structured by IIQ indices with confidence, evidence log, and gating conditions",
  },
  signal_validate: {
    route: "/signal-validate",
    slug: "signal-validate",
    // The company list is the product's page; a company, and so a run, is added on its own.
    formRoute: "/add-company",
    image: "/img/signal-validate.webp",
    description:
      "Rigorous public-signal verification. Decision-grade AI ensures data integrity and authenticity for collaborative confidence.",
  },
  signal_scan_me: {
    route: "/signal-scan-multi-entity",
    slug: "signal-scan-multi-entity",
    image: "/img/signal-scan.webp",
    description:
      "Decision-grade public-signal assessment for multiple entities simultaneously. Structured by IIQ indices with confidence, evidence log, and gating conditions",
  },
};

/**
 * The product a `?return=` slug names, or `null` for anything not in the table. The slug comes
 * back through Stripe in the URL, so it is looked up, never used as a path.
 */
export const productForReturnSlug = (
  slug: string | null | undefined,
): ProductKey | null => {
  if (!slug) return null;
  return PRODUCT_KEYS.find((key) => PRODUCT_PAGES[key].slug === slug) ?? null;
};

/**
 * FALLBACK ONLY. What each run cost when this was written, used solely when
 * `GET /billing/products` cannot be reached (no app-backend in this deployment, a network
 * failure, or an answer that does not read as a catalogue). The backend answers with every
 * product even when Stripe is down, so whenever it can be reached its numbers win. Nothing here
 * can be bought: `purchase` is `null` throughout, so a stale number can never reach a checkout.
 * Keep it in step with app-backend's product table.
 */
export const FALLBACK_PRODUCTS: readonly CatalogueProduct[] = [
  {
    product: "signal_card",
    name: "SignalCard",
    run_credits: 10,
    purchase: null,
  },
  {
    product: "signal_scan",
    name: "SignalScan",
    run_credits: 80,
    purchase: null,
  },
  {
    product: "signal_validate",
    name: "SignalValidate",
    run_credits: 750,
    purchase: null,
  },
  {
    product: "signal_scan_me",
    name: "Multi-Entity SignalScan",
    run_credits: 500,
    included_entities: 6,
    max_entities: 12,
    extra_entity_credits: 80,
    purchase: null,
  },
];

/** The companies a Multi-Entity run is priced by, when the product is priced that way. */
export interface EntityPricing {
  included: number;
  max: number;
  extraCredits: number;
}

export const entityPricingOf = (
  product: CatalogueProduct,
): EntityPricing | null => {
  const { included_entities, max_entities, extra_entity_credits } = product;
  if (
    included_entities === undefined ||
    max_entities === undefined ||
    extra_entity_credits === undefined
  ) {
    return null;
  }
  return {
    included: included_entities,
    max: max_entities,
    extraCredits: extra_entity_credits,
  };
};

/** `companies` pulled into what one run accepts. Products without companies ignore it. */
export const clampCompanies = (
  product: CatalogueProduct,
  companies: number | undefined,
): number => {
  const pricing = entityPricingOf(product);
  if (!pricing) return 0;
  if (companies === undefined || !Number.isFinite(companies)) {
    return pricing.included;
  }
  return Math.min(
    pricing.max,
    Math.max(pricing.included, Math.floor(companies)),
  );
};

/**
 * Credits one run costs. For Multi-Entity SignalScan that depends on how many companies it
 * carries, and anything below the included number costs the same as the included number.
 */
export const runCreditsFor = (
  product: CatalogueProduct,
  companies?: number,
): number => {
  const pricing = entityPricingOf(product);
  if (!pricing) return product.run_credits;
  const extra = clampCompanies(product, companies) - pricing.included;
  return product.run_credits + extra * pricing.extraCredits;
};

/** The cheapest single run across the catalogue, or `null` when it lists nothing. */
export const cheapestRunCredits = (
  products: readonly CatalogueProduct[],
): number | null =>
  products.length === 0
    ? null
    : Math.min(...products.map((product) => product.run_credits));

/* ------------------------------------------------- reading the backend's answer */

const isWholeAtLeast = (value: unknown, min: number): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= min;

const readPrice = (value: unknown): ProductPrice | null => {
  const price = value as Partial<ProductPrice> | null;
  if (
    !price ||
    typeof price.lookup_key !== "string" ||
    price.lookup_key === "" ||
    !isWholeAtLeast(price.amount, 1)
  ) {
    return null;
  }
  return { lookup_key: price.lookup_key, amount: price.amount };
};

/** Thrown for an answer that does not read as the catalogue, so the caller falls back. */
export class UnreadableCatalogueError extends Error {
  constructor(detail: string) {
    super(
      `/billing/products answered something that is not a catalogue: ${detail}`,
    );
    this.name = "UnreadableCatalogueError";
  }
}

/**
 * The catalogue as the page may trust it.
 *
 * A key this page does not know is skipped: it is a product added after this build, with no page
 * to send anyone to. A known product that does not read correctly throws, so the whole answer is
 * treated as unreachable rather than half-trusted. A purchase that does not read is dropped to
 * `null` — the product stays on the page and simply cannot be bought per product — and so is a
 * Multi-Entity purchase without its add-on, which could not price the companies above the
 * included number.
 */
export const readProductCatalogue = (body: unknown): CatalogueProduct[] => {
  const listed = (body as { products?: unknown } | null)?.products;
  if (!Array.isArray(listed)) {
    throw new UnreadableCatalogueError("no products list");
  }

  const products: CatalogueProduct[] = [];
  for (const entry of listed) {
    const raw = entry as Partial<CatalogueProduct> | null;
    if (!raw || !isProductKey(raw.product)) continue;
    if (products.some((known) => known.product === raw.product)) {
      throw new UnreadableCatalogueError(`${raw.product} is listed twice`);
    }
    if (typeof raw.name !== "string" || raw.name.trim() === "") {
      throw new UnreadableCatalogueError(`${raw.product} has no name`);
    }
    if (!isWholeAtLeast(raw.run_credits, 1)) {
      throw new UnreadableCatalogueError(`${raw.product} has no run cost`);
    }

    const product: CatalogueProduct = {
      product: raw.product,
      name: raw.name,
      run_credits: raw.run_credits,
      purchase: null,
    };

    const hasEntities =
      raw.included_entities !== undefined ||
      raw.max_entities !== undefined ||
      raw.extra_entity_credits !== undefined;
    if (hasEntities || raw.product === "signal_scan_me") {
      if (
        !isWholeAtLeast(raw.included_entities, 1) ||
        !isWholeAtLeast(raw.max_entities, raw.included_entities) ||
        !isWholeAtLeast(raw.extra_entity_credits, 1)
      ) {
        throw new UnreadableCatalogueError(
          `${raw.product} has no usable company pricing`,
        );
      }
      product.included_entities = raw.included_entities;
      product.max_entities = raw.max_entities;
      product.extra_entity_credits = raw.extra_entity_credits;
    }

    const price = readPrice(raw.purchase);
    if (price) {
      const pricing = entityPricingOf(product);
      if (pricing && pricing.max > pricing.included) {
        const addon = readPrice((raw.purchase as ProductPurchase).addon);
        product.purchase = addon ? { ...price, addon } : null;
      } else {
        product.purchase = price;
      }
    }

    products.push(product);
  }

  if (products.length === 0) {
    throw new UnreadableCatalogueError("no product this page knows");
  }
  return products;
};
